import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiSave, FiCheckSquare, FiSquare, FiSearch } = FiIcons;

const AssignQuestions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recommendation, setRecommendation] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recRes, questionsRes, relationsRes] = await Promise.all([
        apiService.getRecommendationById(id),
        apiService.getQuestions(),
        apiService.getQuestionRecommendationRelations({ recommendation_id: id })
      ]);

      if (recRes.status === 'success') {
        setRecommendation(recRes.data);
      }
      
      if (questionsRes.status === 'success') {
        setQuestions(questionsRes.data);
      }

      if (relationsRes.status === 'success') {
        setSelectedQuestions(relationsRes.data.map(r => r.question_id));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (questionId) => {
    if (selectedQuestions.includes(questionId)) {
      setSelectedQuestions(selectedQuestions.filter(qid => qid !== questionId));
    } else {
      setSelectedQuestions([...selectedQuestions, questionId]);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // 1. Get existing relations to know what to delete
      const existingRelationsRes = await apiService.getQuestionRecommendationRelations({ recommendation_id: id });
      
      if (existingRelationsRes.status === 'success') {
        // Delete all existing relations for this recommendation
        const deletePromises = existingRelationsRes.data.map(rel => 
          apiService.deleteQuestionRecommendationRelation(rel.id)
        );
        await Promise.all(deletePromises);
      }

      // 2. Create new relations
      const createPromises = selectedQuestions.map(qId => 
        apiService.createQuestionRecommendationRelation({
          question_id: qId,
          recommendation_id: String(id)
        })
      );
      await Promise.all(createPromises);

      alert('Zuordnungen erfolgreich gespeichert');
      navigate('/admin', { state: { activeTab: 'recommendations' } });
    } catch (error) {
      console.error('Error saving assignments:', error);
      alert('Fehler beim Speichern der Zuordnungen');
    } finally {
      setSaving(false);
    }
  };

  const filteredQuestions = questions.filter(q => 
    q.text.toLowerCase().includes(searchTerm.toLowerCase()) || 
    String(q.id).includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (!recommendation) {
    return <div className="p-6 text-red-500">Empfehlung nicht gefunden.</div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/admin', { state: { activeTab: 'recommendations' } })}
            className="flex items-center text-gray-600 hover:text-brand-primary mb-2 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2 w-4 h-4" />
            Zurück zur Übersicht
          </button>
          <h1 className="text-2xl font-bold text-brand-dark">Fragen zuordnen</h1>
          <p className="text-gray-600 mt-1">
            Für Empfehlung: <span className="font-semibold text-brand-primary">{recommendation.title}</span>
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-6 py-2.5 bg-brand-secondary text-white rounded-lg hover:bg-brand-dark transition-colors disabled:opacity-50 shadow-sm"
        >
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <SafeIcon icon={FiSave} className="mr-2 w-4 h-4" />
          )}
          Speichern
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[calc(100vh-200px)]">
        <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <div className="relative w-full max-w-md">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Fragen oder IDs durchsuchen..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-primary text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="text-sm text-gray-500 font-medium">
            {selectedQuestions.length} ausgewählt
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {filteredQuestions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Keine Fragen gefunden.
            </div>
          ) : (
            <div className="space-y-1">
              {filteredQuestions.map((q) => {
                const isSelected = selectedQuestions.includes(q.id);
                return (
                  <div 
                    key={q.id}
                    onClick={() => toggleQuestion(q.id)}
                    className={`flex items-start p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
                      isSelected 
                        ? 'bg-brand-primary/5 border-brand-primary/30' 
                        : 'bg-white border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className={`mt-0.5 mr-3 transition-colors ${isSelected ? 'text-brand-primary' : 'text-gray-300'}`}>
                      <SafeIcon 
                        icon={isSelected ? FiCheckSquare : FiSquare} 
                        className="w-5 h-5" 
                      />
                    </div>
                    <span className={`text-sm leading-relaxed ${isSelected ? 'text-brand-dark font-medium' : 'text-gray-600'}`}>
                      <span className="font-mono text-xs text-gray-400 mr-2">#{q.id}</span>
                      {q.text}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignQuestions;